import React from 'react';
import './WallsLayer.css';
import type { Wall, Table } from '../../types';
import { useWallDrawing } from '../../hooks/useWallDrawing';

// WallsLayer renders existing walls and a preview line during drawing.
export interface WallsLayerProps {
  mode: 'wall' | 'delete' | null;
  readOnly?: boolean;
  walls: Wall[];
  setWalls: React.Dispatch<React.SetStateAction<Wall[]>>;
  tables: Table[];
  pushHistory: (nextTables: Table[], nextWalls: Wall[], description?: string) => void;
  saveScenario: (
    silent?: boolean,
    overrides?: {
      walls?: Wall[];
    },
    options?: {
      historyDescription?: string | null;
    },
  ) => void;
  currentWallColor: string;
  gridRef: React.RefObject<HTMLDivElement>;
  uniqueId: () => string;
}

const WallsLayer: React.FC<WallsLayerProps> = ({
  mode,
  readOnly = false,
  walls,
  setWalls,
  tables,
  pushHistory,
  saveScenario,
  currentWallColor,
  gridRef,
  uniqueId,
}) => {
  // Track whether a wall is currently being drawn and its start/end points.
  const {
    isDrawing,
    draftStart,
    draftEnd,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    deleteWall,
  } = useWallDrawing({
    mode,
    tables,
    walls,
    setWalls,
    pushHistory,
    saveScenario,
    gridRef,
    uniqueId,
  });

  // Finish drawing: add a new wall if the line has non‑zero length


  // Determine the SVG width and height from the grid container
  const width = gridRef.current ? gridRef.current.clientWidth : 0;
  const height = gridRef.current ? gridRef.current.clientHeight : 0;

  // Create SVG line elements for each existing wall
  const wallLines = walls.map(wall => {
    const x1 = wall.x1 * width;
    const y1 = wall.y1 * height;
    const x2 = wall.x2 * width;
    const y2 = wall.y2 * height;
    return (
      <line
        key={wall.id}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={currentWallColor}
        strokeWidth={2}
        onPointerDown={(e) => {
          // In delete mode, remove the wall on pointer down
          if (!readOnly && mode === 'delete') {
            e.stopPropagation();
            deleteWall(wall.id);
          }
        }}
        style={{ cursor: !readOnly && mode === 'delete' ? 'not-allowed' : 'default', pointerEvents: !readOnly && mode === 'delete' ? 'auto' : 'none' }}
      />
    );
  });

  // Add a preview line if currently drawing
  if (mode === 'wall' && isDrawing && draftStart && draftEnd) {
    const x1p = draftStart.x * width;
    const y1p = draftStart.y * height;
    const x2p = draftEnd.x * width;
    const y2p = draftEnd.y * height;
    wallLines.push(
      <line
        key="preview"
        x1={x1p}
        y1={y1p}
        x2={x2p}
        y2={y2p}
        stroke={currentWallColor + '80'}
        strokeWidth={2}
        strokeDasharray="4,2"
      />
    );
  }

  return (
    <>
      {/* Render walls and optional preview as an SVG overlay */}
      <svg
        className="walls-layer"
        width={width}
        height={height}
        style={{ position: 'absolute', left: 0, top: 0, zIndex: 5, pointerEvents: 'none' }}
      >
        {wallLines}
      </svg>
      {/* Overlay capturing pointer events when drawing walls */}
      {!readOnly && mode === 'wall' && (
        <div
          className="wall-overlay"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        />
      )}
    </>
  );
};

export default WallsLayer;
